import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StaffPageShell from '../components/StaffPageShell';
import { ThemeButton } from '../components/ThemeWrapper';
import usePermissions from '../hooks/usePermissions';
import usePmaStudentImport from '../hooks/usePmaStudentImport';
import usePmaStudentPicker from '../hooks/usePmaStudentPicker';
import useReservedUnixStudents from '../hooks/useReservedUnixStudents';
import PmaStudentPicker from '../components/PmaStudentPicker';
import ReservedUnixStudentsPanel from '../components/ReservedUnixStudentsPanel';
import PmaIdentityPolicyForm from '../components/PmaIdentityPolicyForm';
import PmaExcelPreview from '../components/PmaExcelPreview';
import PmaProvisionDebug from '../components/PmaProvisionDebug';
import PmaCreateProgressBar from '../components/PmaCreateProgressBar';
import LabCapacityPanel from '../components/LabCapacityPanel';
import LabUserInventoryPanel from '../components/LabUserInventoryPanel';
import useLabCapacity from '../hooks/useLabCapacity';
import useLabUserInventory from '../hooks/useLabUserInventory';
import {
  inferPmaPolicyFromStudents,
  mergePolicyWithStudentHints,
} from '../domain/pma/pmaPolicyFromStudent';

const PmaStudentImportPage = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const {
    file,
    downloading,
    uploading,
    error,
    result,
    preview,
    previewing,
    generateUserGen,
    downloadUserGen,
    previewTemplate,
    downloadTemplate,
    sendToPma,
    sendingToPma,
    deleteFromLab,
    deletingFromLab,
    setLabAccess,
    accessBusy,
    createProgress,
    pmaSendResult,
    pmaV2Configured,
    labCreateMeta,
    labDeleteMeta,
    labAccessMeta,
    chooseFile,
    uploadFile,
    policy,
    setPolicy,
  } = usePmaStudentImport();
  const picker = usePmaStudentPicker();
  const {
    capacity,
    loading: capacityLoading,
    error: capacityError,
    ingesting,
    running,
    ingestError,
    ingestMessage,
    ingestFile,
    runDiscovery,
    progress,
    exception,
    elapsedMs,
  } = useLabCapacity();
  const {
    inventory,
    loading: inventoryLoading,
    error: inventoryError,
    running: inventoryRunning,
    runError: inventoryRunError,
    runMessage: inventoryRunMessage,
    runInventory,
    progress: inventoryProgress,
    exception: inventoryException,
    elapsedMs: inventoryElapsedMs,
  } = useLabUserInventory();
  const reservedUnix = useReservedUnixStudents();

  useEffect(() => {
    if (!hasPermission('import.pma')) {
      navigate('/dashboard');
    }
  }, [hasPermission, navigate]);

  useEffect(() => {
    if (!picker.selectedStudents.length) return;
    const hints = inferPmaPolicyFromStudents(picker.selectedStudents);
    if (!Object.keys(hints).length) return;
    setPolicy((prev) => mergePolicyWithStudentHints(prev, hints));
  }, [picker.selectedStudents, setPolicy]);

  if (!hasPermission('import.pma')) return null;

  return (
    <StaffPageShell
      title="Lab Linux users"
      subtitle="SMS creates, disables, enables, and deletes lab Linux users. Disable locks the login and stops VNC; Enable turns that back on. Delete removes the account."
    >
      <div className="profile-card theme-animate p-6 space-y-6">
        <p className="text-sm theme-text-secondary">
          Disable and Enable follow the PMA cycle: the home directory stays. FQDN{' '}
          <code>host:port</code> uses that port for SSH. Default password is{' '}
          <code>TempPass456!</code>. Staff access is under{' '}
          <Link className="underline" to="/admin/access-management">
            Access management
          </Link>
          .
        </p>

        <PmaIdentityPolicyForm policy={policy} onChange={setPolicy} />

        <LabCapacityPanel
          capacity={capacity}
          loading={capacityLoading}
          error={capacityError}
          ingesting={ingesting}
          running={running}
          ingestError={ingestError}
          ingestMessage={ingestMessage}
          onIngestFile={ingestFile}
          onRunDiscovery={runDiscovery}
          progress={progress}
          exception={exception}
          elapsedMs={elapsedMs}
          extraBusy={inventoryRunning}
          onApplySuggestion={(suggestion) => {
            if (!suggestion?.fqdn) return;
            setPolicy((current) => ({
              ...current,
              vncFqdn: suggestion.fqdn,
              puttyFqdn: suggestion.fqdn,
            }));
          }}
        />

        <LabUserInventoryPanel
          inventory={inventory}
          loading={inventoryLoading}
          error={inventoryError}
          running={inventoryRunning}
          runError={inventoryRunError}
          runMessage={inventoryRunMessage}
          onRunInventory={runInventory}
          progress={inventoryProgress}
          exception={inventoryException}
          elapsedMs={inventoryElapsedMs}
          extraBusy={running}
        />

        <PmaStudentPicker
          query={picker.query}
          onQueryChange={picker.setQuery}
          students={picker.students}
          selectedSet={picker.selectedSet}
          selectedCount={picker.selectedCount}
          loading={picker.loading}
          error={picker.error}
          onToggle={picker.toggleStudent}
          onToggleVisible={picker.toggleVisible}
          onClear={picker.clearSelection}
        />

        {labCreateMeta && (
          <p className="text-sm theme-text-secondary">
            Lab create:{' '}
            {labCreateMeta.enabled ? (labCreateMeta.dryRun ? 'dry-run' : 'enabled') : 'disabled'}
            {labCreateMeta.configured ? '' : ' · SSH key not configured'}
            {` · ssh user ${labCreateMeta.sshUser || 'sms'}`}
            {labDeleteMeta
              ? ` · delete ${
                  labDeleteMeta.enabled
                    ? labDeleteMeta.dryRun
                      ? 'dry-run'
                      : 'enabled'
                    : 'disabled'
                }`
              : ''}
            {labAccessMeta
              ? ` · disable/enable ${
                  labAccessMeta.enabled
                    ? labAccessMeta.dryRun
                      ? 'dry-run'
                      : 'enabled'
                    : 'disabled'
                }`
              : ''}
          </p>
        )}
        {pmaV2Configured === false && !labCreateMeta && (
          <p className="text-sm theme-text-secondary">
            PMA token is optional now. Linux create uses SMS_LAB_CREATE_ENABLED and an SSH key on the
            SMS backend.
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          <ThemeButton
            type="button"
            onClick={() => sendToPma(picker.selectedIds, picker.selectedStudents)}
            disabled={sendingToPma || deletingFromLab || Boolean(accessBusy) || downloading || previewing}
          >
            {sendingToPma
              ? createProgress.currentUser
                ? `Creating ${createProgress.currentUser}…`
                : 'Creating on lab servers…'
              : 'Create accounts on lab servers'}
          </ThemeButton>
          <ThemeButton
            type="button"
            onClick={() => deleteFromLab(picker.selectedIds, picker.selectedStudents)}
            disabled={deletingFromLab || sendingToPma || Boolean(accessBusy) || downloading || previewing}
          >
            {deletingFromLab
              ? createProgress.currentUser
                ? `Deleting ${createProgress.currentUser}…`
                : 'Deleting on lab servers…'
              : 'Delete accounts + VNC'}
          </ThemeButton>
          <ThemeButton
            type="button"
            onClick={() => setLabAccess('disable', picker.selectedIds, picker.selectedStudents)}
            disabled={Boolean(accessBusy) || deletingFromLab || sendingToPma || downloading || previewing}
          >
            {accessBusy === 'disable'
              ? createProgress.currentUser
                ? `Disabling ${createProgress.currentUser}…`
                : 'Disabling on lab servers…'
              : 'Disable accounts'}
          </ThemeButton>
          <ThemeButton
            type="button"
            onClick={() => setLabAccess('enable', picker.selectedIds, picker.selectedStudents)}
            disabled={Boolean(accessBusy) || deletingFromLab || sendingToPma || downloading || previewing}
          >
            {accessBusy === 'enable'
              ? createProgress.currentUser
                ? `Enabling ${createProgress.currentUser}…`
                : 'Enabling on lab servers…'
              : 'Enable accounts'}
          </ThemeButton>
          <ThemeButton
            type="button"
            onClick={() => generateUserGen(picker.selectedIds)}
            disabled={previewing || downloading || sendingToPma || deletingFromLab || Boolean(accessBusy)}
          >
            {previewing ? 'Generating…' : 'Validate in SMS only'}
          </ThemeButton>
          <ThemeButton
            type="button"
            onClick={() => downloadUserGen(picker.selectedIds)}
            disabled={downloading || previewing}
          >
            {downloading ? 'Preparing Excel…' : 'Download UserGen.xlsx'}
          </ThemeButton>
          <ThemeButton
            type="button"
            onClick={() => previewTemplate(picker.selectedIds)}
            disabled={previewing || downloading}
          >
            {previewing ? 'Building preview…' : 'Preview Excel'}
          </ThemeButton>
          <ThemeButton
            type="button"
            onClick={() => downloadTemplate(picker.selectedIds)}
            disabled={downloading}
          >
            {downloading
              ? 'Preparing Excel…'
              : picker.selectedCount
                ? `Download Excel for ${picker.selectedCount} student${picker.selectedCount === 1 ? '' : 's'}`
                : 'Download blank Excel template'}
          </ThemeButton>
        </div>

        <PmaCreateProgressBar
          active={createProgress.active || sendingToPma || deletingFromLab || Boolean(accessBusy)}
          percent={createProgress.percent}
          label={createProgress.label}
          done={createProgress.done}
          currentUser={createProgress.currentUser}
          index={createProgress.index}
          total={createProgress.total}
          completedNames={createProgress.completedNames}
        />

        {(error || pmaSendResult) && (
          <div className="space-y-3">
            {error && (
              <div
                className="p-3 rounded-lg text-sm whitespace-pre-wrap font-mono"
                style={{ backgroundColor: 'var(--color-error-100)', color: 'var(--color-error)' }}
              >
                {error}
              </div>
            )}
            {pmaSendResult && (
              <div
                className="p-3 rounded-lg text-sm theme-text space-y-1"
                style={{ border: '1px solid var(--color-border)' }}
              >
                <p className="font-medium">
                  {pmaSendResult.message || 'Lab create/delete finished'}
                </p>
                <p className="theme-text-secondary">
                  SMS {pmaSendResult.sms?.successCount ?? pmaSendResult.successCount ?? 0} ok
                  {' · '}
                  {pmaSendResult.sms?.errorCount ?? pmaSendResult.errorCount ?? 0} failed
                  {pmaSendResult.pma?.status != null ? ` · PMA HTTP ${pmaSendResult.pma.status}` : ''}
                </p>
                {pmaSendResult.pma?.message && (
                  <p className="theme-text-secondary">PMA notify: {pmaSendResult.pma.message}</p>
                )}
                <PmaProvisionDebug result={pmaSendResult} />
              </div>
            )}
          </div>
        )}

        <PmaExcelPreview preview={preview} loading={previewing} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            uploadFile();
          }}
          className="space-y-3"
        >
          <label className="block text-sm theme-text">
            Upload completed .xlsx
            <input
              className="block mt-2 theme-text"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => chooseFile(e.target.files?.[0] || null)}
            />
          </label>
          {file && <p className="text-sm theme-text-secondary">Selected: {file.name}</p>}
          <ThemeButton type="submit" disabled={uploading || !file}>
            {uploading ? 'Importing students…' : 'Upload and import students'}
          </ThemeButton>
        </form>

        {result && (
          <div className="space-y-2">
            <p className="theme-text font-medium">{result.message}</p>
            <p className="text-sm theme-text-secondary">
              {result.successCount} imported
              {(result.successes || []).some((s) => s.updated) ? ' (includes updates)' : ''}
              {' · '}
              {result.errorCount} failed · {result.totalRows} rows
            </p>
            {result.errors?.length > 0 && (
              <ul className="text-sm space-y-1" style={{ color: 'var(--color-error)' }}>
                {result.errors.map((item) => (
                  <li key={`${item.rowNumber}-${item.email || 'row'}`}>
                    Row {item.rowNumber}
                    {item.email ? ` (${item.email})` : ''}: {item.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <ReservedUnixStudentsPanel
        form={reservedUnix.form}
        setField={reservedUnix.setField}
        presetId={reservedUnix.presetId}
        applyPreset={reservedUnix.applyPreset}
        presets={reservedUnix.presets}
        summary={reservedUnix.summary}
        preview={reservedUnix.preview}
        result={reservedUnix.result}
        error={reservedUnix.error}
        previewing={reservedUnix.previewing}
        importing={reservedUnix.importing}
        onPreview={reservedUnix.previewPlan}
        onImport={reservedUnix.importPlan}
      />
    </StaffPageShell>
  );
};

export default PmaStudentImportPage;
